import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Eye, EyeOff, ChevronDown, ArrowLeft, Loader2, AlertCircle, CheckCircle2, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TaxSetu Logo - Infinity symbol
const TAXSETU_LOGO = "https://customer-assets.emergentagent.com/job_7a5b9feb-7efd-48fe-91ba-ada6a1b3fe62/artifacts/dbzfuj5b_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

/**
 * GST Portal Login Component
 * Replicates the Demo GST Portal login screen style
 * With secure access: verifies username/password from email, then shows OTP modal
 */
const GSTPortalLogin = ({ onLogin, formType = 'GSTR-1', accessEmail = '', useSecureAccess = false }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState(accessEmail || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // OTP Modal state
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifiedCredentials, setVerifiedCredentials] = useState(null);
  
  // Resend credentials state
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleBack = () => {
    navigate('/gst');
  };
  
  // Resend credentials to email
  const handleResendCredentials = async () => {
    if (!username.trim()) {
      setError('Please enter your email/username first');
      return;
    }
    
    setResendLoading(true);
    setError('');
    setResendSuccess(false);
    
    try {
      const response = await fetch(`${API_URL}/api/gstr1-access/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: username.trim() })
      });
      
      if (response.ok) {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000); // Hide after 5 seconds
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to resend credentials');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim()) {
      setError('Please enter Username');
      return;
    }
    if (!password.trim()) {
      setError('Please enter Password');
      return;
    }
    
    // If using secure access, verify with backend
    if (useSecureAccess) {
      setLoading(true);
      setError('');
      
      try {
        // Check if credentials are valid (without activating - just checking)
        const response = await fetch(`${API_URL}/api/gstr1-access/check/${encodeURIComponent(username.trim())}`);
        const data = await response.json();
        
        if (!data.has_access && data.reason === 'not_found') {
          setError('No account found for this email. Please go back and register first.');
          setLoading(false);
          return;
        }
        
        if (data.reason === 'expired') {
          setError('Your access has expired. Please go back and register again.');
          setLoading(false);
          return;
        }
        
        // Store credentials for OTP verification
        setVerifiedCredentials({ username: username.trim(), password: password.trim() });
        setShowOtpModal(true);
        
      } catch (err) {
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      // Legacy mode - check for admin/admin
      if (username.trim() !== 'admin' || password.trim() !== 'admin') {
        setError('Invalid credentials. Use admin / admin');
        return;
      }
      setError('');
      onLogin({ username, password });
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    
    if (!otp.trim() || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }
    
    setOtpLoading(true);
    setOtpError('');
    
    try {
      // Activate access with OTP
      const response = await fetch(`${API_URL}/api/gstr1-access/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: verifiedCredentials.username,
          password: verifiedCredentials.password,
          otp: otp.trim()
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setOtpError(data.detail || 'Invalid OTP. Please check your email.');
        setOtpLoading(false);
        return;
      }
      
      // Save access info
      localStorage.setItem('gstr1_access_email', verifiedCredentials.username);
      localStorage.setItem('gstr1_access_password', verifiedCredentials.password);
      localStorage.setItem('gstr1_access_expires', data.expires_at);
      
      // Success - proceed to portal
      setShowOtpModal(false);
      onLogin(verifiedCredentials);
      
    } catch (err) {
      setOtpError('Network error. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Skip to Main Content Bar with Back Button */}
      <div className="bg-[#0b1e3f] text-white text-xs py-1.5 px-4 flex justify-between items-center">
        <button 
          onClick={handleBack}
          className="flex items-center gap-1.5 px-3 py-1 bg-white/10 hover:bg-white/20 rounded text-sm transition-colors border border-white/20"
          data-testid="gst-login-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back</span>
        </button>
        
        <div className="flex items-center gap-3">
          <span className="text-gray-300 cursor-pointer hover:underline">Skip to Main Content</span>
          <span className="flex items-center gap-2 text-gray-300">
            <span className="w-5 h-5 bg-yellow-500 rounded text-black text-xs flex items-center justify-center font-bold">0</span>
            <span className="cursor-pointer">A<sup>+</sup></span>
            <span className="cursor-pointer">A<sup>-</sup></span>
          </span>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-[#0b1e3f] text-white py-6 px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-5">
            <div className="h-14 flex-shrink-0">
              <img 
                src={TAXSETU_LOGO}
                alt="PractiQ"
                className="h-full object-contain"
              />
            </div>
            <div className="border-l border-white/30 pl-5">
              <h1 className="text-2xl font-normal tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
                Demo Goods and Services Tax
              </h1>
              <p className="text-base text-gray-300 mt-1">
                Practical Simulation - Not a Government Website - Only for Educational Purpose
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" className="bg-white text-[#0b1e3f] border-white hover:bg-gray-100 font-medium">
              REGISTER
            </Button>
            <Button className="bg-white text-[#0b1e3f] hover:bg-gray-100 font-medium">
              LOGIN
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation Bar */}
      <nav className="bg-[#2a5298]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center">
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">Home</button>
            <button className="px-5 py-3 bg-[#00bcd4] text-white font-medium text-sm flex items-center gap-1">
              Services <ChevronDown className="w-3 h-3" />
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">GST Law</button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm flex items-center gap-1">
              Downloads <ChevronDown className="w-3 h-3" />
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm flex items-center gap-1">
              Search Taxpayer <ChevronDown className="w-3 h-3" />
            </button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">Help and Taxpayer Facilities</button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">e-Invoice</button>
            <button className="px-5 py-3 text-white hover:bg-white/10 text-sm">News and Updates</button>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="bg-white border-b py-2 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-blue-600 cursor-pointer hover:underline">Home</span>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600">Login</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-md mx-auto bg-white rounded shadow-sm border">
            <div className="border-b px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">Login</h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <p className="text-sm text-right mb-4">
                <span className="text-red-500">*</span>
                <span className="text-gray-600"> indicates mandatory fields</span>
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username<span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter Username"
                  className="w-full border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                  data-testid="gst-username-input"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password<span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter Password"
                    className="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500 pr-10"
                    data-testid="gst-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="bg-[#0b1e3f] hover:bg-[#1a3a5c] text-white px-8"
                disabled={loading}
                data-testid="gst-login-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                ) : (
                  'LOGIN'
                )}
              </Button>

              <div className="flex justify-between mt-6">
                <a href="#" className="text-blue-600 text-sm hover:underline">Forgot Username</a>
                <a href="#" className="text-blue-600 text-sm hover:underline">Forgot Password</a>
              </div>

              <div className="mt-6 pt-4 border-t">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">ⓘ First time login:</span> If you are logging in for the first time, click{' '}
                  <a href="#" className="text-blue-600 hover:underline">here</a> to log in.
                </p>
              </div>
            </form>
          </div>

          {/* Simulator Notice */}
          <div className="max-w-md mx-auto mt-4 p-4 bg-blue-50 border border-blue-200 rounded">
            <p className="text-sm text-blue-800 text-center mb-3">
              <strong>📧 Check your email</strong> for Username and Password to login.
            </p>
            
            {resendSuccess && (
              <div className="flex items-center justify-center gap-2 text-green-600 text-sm bg-green-50 p-2 rounded mb-3">
                <CheckCircle2 className="w-4 h-4" />
                Credentials sent to your email!
              </div>
            )}
            
            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCredentials}
                disabled={resendLoading}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="resend-credentials-btn"
              >
                {resendLoading ? (
                  <span className="flex items-center gap-1 justify-center">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  "Didn't receive email? Click to Resend Credentials"
                )}
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#2a5298] text-white py-4 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-xs text-white/80 mb-2">
            This is a Practice Simulation Portal by PractiQ for educational purposes only.
          </p>
          <div className="flex flex-wrap items-center justify-between text-xs text-white/60">
            <span>© 2024-25 PractiQ - EdTech Platform</span>
            <span>Site Last Updated on {new Date().toLocaleDateString('en-GB')}</span>
            <span>For Practice Purpose Only</span>
          </div>
        </div>
      </footer>

      {/* OTP Modal */}
      <Dialog open={showOtpModal} onOpenChange={setShowOtpModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
              Enter OTP to Verify
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleOtpSubmit} className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              Please enter the 6-digit OTP sent to your email to complete login.
            </p>
            
            <div>
              <Input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                className="text-center text-2xl tracking-[0.5em] font-mono"
                maxLength={6}
                autoFocus
                data-testid="gst-otp-input"
              />
            </div>
            
            {otpError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {otpError}
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full bg-[#0b1e3f] hover:bg-[#1a3a5c]"
              disabled={otpLoading || otp.length !== 6}
              data-testid="gst-otp-submit-btn"
            >
              {otpLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying OTP...</>
              ) : (
                'Verify & Login'
              )}
            </Button>
            
            <p className="text-xs text-gray-500 text-center">
              OTP was sent to: <strong>{verifiedCredentials?.username}</strong>
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Scroll to top button */}
      <button 
        className="fixed bottom-4 right-4 w-10 h-10 bg-[#0b1e3f] text-white rounded flex items-center justify-center shadow-lg hover:bg-[#1a3a5c]"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <ChevronDown className="w-5 h-5 rotate-180" />
      </button>
    </div>
  );
};

export default GSTPortalLogin;
